import { Preferences } from '@capacitor/preferences';

/**
 * MOTOPIKIS Pro - High Availability Storage Service
 * Implements the 4-pillar migration strategy:
 * 1. Abstraction (Unified API)
 * 2. Block Migration (Boot-time sync)
 * 3. Double Write (Hybrid Safety)
 * 4. Sync Cache (Performance)
 */
class StorageService {
    private cache: Record<string, string | null> = {};
    private isInitialized = false;

    /**
     * Initializes the storage service. 
     * Must be called during App startup (Splash Screen).
     */
    async init() {
        if (this.isInitialized) return;

        // Solo usar Preferences si estamos en un entorno nativo o si Capacitor lo soporta en web
        const isNative = window.hasOwnProperty('Capacitor') && (window as any).Capacitor.isNativePlatform();

        try {
            if (!isNative) {
                console.log("[Storage] Web environment detected. Using LocalStorage only.");
                this.isInitialized = true;
                return;
            }
            // PILLAR 2: Migration in Block
            // Check if we have already migrated
            const { value: migrationDone } = await Preferences.get({ key: 'motopikis_migration_complete' });

            if (!migrationDone) {
                console.log("[Storage] Starting migration from LocalStorage to Preferences...");
                // Migrate the most important keys
                const keysToMigrate = Object.keys(localStorage).filter(k => k.startsWith('motopikis_'));
                for (const key of keysToMigrate) {
                    const val = localStorage.getItem(key);
                    if (val) {
                        await Preferences.set({ key, value: val });
                        this.cache[key] = val;
                    }
                }
                await Preferences.set({ key: 'motopikis_migration_complete', value: 'true' });
            }

            // Load all current Preferences into memory cache for Synchronous access
            const { keys } = await Preferences.getKeys();
            for (const key of keys) {
                const { value } = await Preferences.get({ key });
                this.cache[key] = value;
            }

            this.isInitialized = true;
            console.log("[Storage] Service initialized with", Object.keys(this.cache).length, "keys.");
        } catch (e) {
            console.error("[Storage] Critical failure during initialization:", e);
            // Fallback to LocalStorage as safety net
            this.isInitialized = true;
        }
    }

    /**
     * Synchronous access (from memory cache). 
     * Safe to use in React render/hooks if init() was called at startup.
     */
    get(key: string): string | null {
        // Pillar 3: Double read check (safety first)
        return this.cache[key] || localStorage.getItem(key);
    }

    /**
     * Asynchronous save with Double Write.
     */
    async set(key: string, value: string) {
        this.cache[key] = value;
        localStorage.setItem(key, value);
        
        const isNative = window.hasOwnProperty('Capacitor') && (window as any).Capacitor.isNativePlatform();
        if (isNative) {
            try {
                await Preferences.set({ key, value });
            } catch (e) {
                console.error("[Storage] Error saving to Native:", key, e);
            }
        }
    }

    /**
     * Removes a key from all storages.
     */
    async remove(key: string) {
        delete this.cache[key];
        localStorage.removeItem(key);
        
        const isNative = window.hasOwnProperty('Capacitor') && (window as any).Capacitor.isNativePlatform();
        if (isNative) {
            await Preferences.remove({ key });
        }
    }

    /**
     * Clears all MotoPikis data.
     */
    async clear() {
        this.cache = {};
        
        const isNative = window.hasOwnProperty('Capacitor') && (window as any).Capacitor.isNativePlatform();
        if (isNative) {
            const { keys } = await Preferences.getKeys();
            for (const k of keys) {
                if (k.startsWith('motopikis_')) await Preferences.remove({ key: k });
            }
        }

        // Selective clear for localStorage
        Object.keys(localStorage).forEach(k => {
            if (k.startsWith('motopikis_')) localStorage.removeItem(k);
        });
    }
}

export const Storage = new StorageService();
